import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Section, SectionHeader } from "../components/Section";
import { Card } from "../components/Card";
import { CTASection } from "../components/CTASection";
import { ANALYTICS_SUMMARIES } from "../lib/content";
import { useTranslation } from "../lib/i18n";

function StatBar({ label, value, max, suffix }: { label: string; value: number; max: number; suffix?: string }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.25rem" }}>
        <span style={{ fontSize: "0.8125rem", fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>
          {value}{suffix}
        </span>
      </div>
      <div
        style={{
          height: "6px",
          borderRadius: "var(--radius-full)",
          background: "var(--neutral-100)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            borderRadius: "var(--radius-full)",
            background: "linear-gradient(90deg, var(--accent-400), var(--accent-600))",
            transition: "width var(--transition-slow)",
          }}
        />
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const { t } = useTranslation();

  return (
    <>
      <SEO
        title="Analytics Overview — Sentinel"
        description="Public high-level summaries of domain safety analytics, intervention statistics, diagnostic trends, operator performance, certification progress, and compliance tracking."
        canonical="/analytics"
        image="https://images.pexels.com/photos/590016/pexels-photo-590016.jpeg?auto=compress&cs=tinysrgb&w=1200"
      />
      <PageHero
        eyebrow={t("analytics.page.eyebrow")}
        title={t("analytics.page.title")}
        description={t("analytics.page.description")}
        bgVariant="teal"
        bgImage="https://images.pexels.com/photos/590016/pexels-photo-590016.jpeg?auto=compress&cs=tinysrgb&w=1600"
      />

      <Section>
        <SectionHeader
          eyebrow={t("analytics.summaries.eyebrow")}
          title={t("analytics.summaries.title")}
          description={t("analytics.summaries.description")}
        />
        <div className="grid grid-2">
          {ANALYTICS_SUMMARIES.map((s) => (
            <Card key={s.domain} hover>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <h3 style={{ fontSize: "1.125rem" }}>{s.domain}</h3>
                <span className="badge badge-accent">{t("common.active")}</span>
              </div>

              <div className="grid grid-2" style={{ gap: "var(--space-3)", marginBottom: "var(--space-3)" }}>
                <div>
                  <span className="eyebrow" style={{ fontSize: "0.75rem" }}>{t("analytics.stat.interventions")}</span>
                  <p style={{ fontSize: "1.5rem", fontWeight: 800, marginTop: "0.25rem" }}>
                    {s.interventionsExecuted.toLocaleString()}
                  </p>
                </div>
                <div>
                  <span className="eyebrow" style={{ fontSize: "0.75rem" }}>{t("analytics.stat.accuracy")}</span>
                  <p style={{ fontSize: "1.5rem", fontWeight: 800, marginTop: "0.25rem" }}>
                    {s.diagnosticAccuracy}%
                  </p>
                </div>
                <div>
                  <span className="eyebrow" style={{ fontSize: "0.75rem" }}>{t("analytics.stat.mttDetect")}</span>
                  <p style={{ fontSize: "1.25rem", fontWeight: 700, marginTop: "0.25rem" }}>{s.meanTimeToDetect}</p>
                </div>
                <div>
                  <span className="eyebrow" style={{ fontSize: "0.75rem" }}>{t("analytics.stat.mttIntervene")}</span>
                  <p style={{ fontSize: "1.25rem", fontWeight: 700, marginTop: "0.25rem" }}>{s.meanTimeToIntervene}</p>
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <StatBar label={t("analytics.stat.operatorPerf")} value={s.operatorPerformance} max={100} suffix="%" />
                <StatBar label={t("analytics.stat.certProgress")} value={s.certificationProgress} max={100} suffix="%" />
                <StatBar label={t("analytics.stat.complianceScore")} value={s.complianceScore} max={100} suffix="%" />
              </div>
            </Card>
          ))}
        </div>
      </Section>

      <Section variant="subtle">
        <SectionHeader
          eyebrow={t("analytics.tracked.eyebrow")}
          title={t("analytics.tracked.title")}
        />
        <div className="grid grid-3">
          {[
            { t: t("analytics.tracked1.title"), d: t("analytics.tracked1.desc") },
            { t: t("analytics.tracked2.title"), d: t("analytics.tracked2.desc") },
            { t: t("analytics.tracked3.title"), d: t("analytics.tracked3.desc") },
            { t: t("analytics.tracked4.title"), d: t("analytics.tracked4.desc") },
            { t: t("analytics.tracked5.title"), d: t("analytics.tracked5.desc") },
            { t: t("analytics.tracked6.title"), d: t("analytics.tracked6.desc") },
          ].map((item) => (
            <Card key={item.t}>
              <h3 style={{ fontSize: "1rem", marginBottom: "0.5rem" }}>{item.t}</h3>
              <p className="text-muted" style={{ fontSize: "0.9375rem" }}>{item.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <CTASection
        title={t("analytics.cta.title")}
        description={t("analytics.cta.description")}
        primaryLabel={t("cta.logInToPortal")}
        primaryTo="/login"
        secondaryLabel={t("cta.registerNow")}
        secondaryTo="/register"
      />
    </>
  );
}
